import { Badge } from '@/components/ui/badge';
import { UserCell } from '@components/UserCell';
import { useTranslation } from '@hooks/useTranslation';
import { formatAmount } from '../format';
import { KIND_INFO } from './priceModifierTypes';
import type { PriceModifierKind, PriceModifierRecord } from './priceModifierTypes';

interface PriceModifierOverviewSectionProps {
  record: PriceModifierRecord;
  kind: PriceModifierKind;
}

const DASH = '—';

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="font-inter text-sm font-semibold text-grey">{children}</span>;
}

function FieldValue({ children }: { children: React.ReactNode }) {
  return <p className="font-inter text-sm text-off-black mt-2">{children}</p>;
}

function YesNo({ value }: { value: boolean }) {
  const { t } = useTranslation();
  return <FieldValue>{value ? t('common.yes_label') : t('common.no_label')}</FieldValue>;
}

export function PriceModifierOverviewSection({ record, kind }: PriceModifierOverviewSectionProps) {
  const info = KIND_INFO[kind];

  return (
    <div>
      <h3 className="font-kapra-expanded text-2xl font-black uppercase leading-tight text-off-black mb-4">
        {info.title}
      </h3>

      <div className="flex flex-col gap-8 rounded-input border border-dinnerplate-white bg-off-white p-6">
        <div>
          <FieldLabel>Name</FieldLabel>
          <p className="font-inter text-sm font-semibold text-off-black mt-2">{record.name}</p>
        </div>

        <div>
          <FieldLabel>Description</FieldLabel>
          <FieldValue>{record.description ?? DASH}</FieldValue>
        </div>

        <div className="flex gap-16">
          <div className="w-preview-field shrink-0">
            <FieldLabel>Amount</FieldLabel>
            <FieldValue>{formatAmount(record.amount, record.calculation_type)}</FieldValue>
          </div>
          <div>
            <FieldLabel>Owner</FieldLabel>
            <div className="mt-2">
              <UserCell user={record.user} truncateAfter={20} />
            </div>
          </div>
        </div>

        <div>
          <FieldLabel>Auto-applied</FieldLabel>
          <YesNo value={record.auto_applied} />
        </div>

        {record.auto_applied && (
          <div className="flex flex-col gap-8 rounded-input border border-dinnerplate-white p-6">
            <h4 className="font-inter text-base font-semibold text-off-black">
              Auto-apply conditions
            </h4>

            <div>
              <FieldLabel>Event types</FieldLabel>
              <div className="mt-2 flex flex-wrap gap-2">
                {record.event_types.length > 0 ? (
                  record.event_types.map((type) => (
                    <Badge
                      key={type}
                      variant="static"
                      className="font-inter w-fit rounded-xs border border-off-black bg-transparent text-off-black text-xs font-normal px-1.5 py-1 leading-tight-sm"
                    >
                      {type}
                    </Badge>
                  ))
                ) : (
                  <span className="font-inter text-sm text-grey">{DASH}</span>
                )}
              </div>
            </div>

            <div className="flex gap-16">
              <div className="w-preview-field shrink-0">
                <FieldLabel>Pax range</FieldLabel>
                <FieldValue>{`${record.min_pax}–${record.max_pax}`}</FieldValue>
              </div>
              {kind === 'surcharge' && 'has_stairs' in record && (
                <div>
                  <FieldLabel>Has stairs</FieldLabel>
                  <YesNo value={record.has_stairs} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
